import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  ADMIN_PROSPECT_ACTIVE_OPPORTUNITY_STATUSES,
  ADMIN_PROSPECT_FOLLOW_UP_BUCKETS,
  ADMIN_PROSPECT_FOLLOW_UP_WORKSPACE_PAGE_SIZE,
  ADMIN_PROSPECT_PUBLIC_RESPONSE_PROCESSING_LIST_STATES,
  buildListAdminProspectActiveOpportunitiesRequest,
  buildListAdminProspectFollowUpsRequest,
  buildListAdminProspectResponsesToProcessRequest,
  listAdminProspectActiveOpportunities,
  listAdminProspectFollowUps,
  listAdminProspectResponsesToProcess,
  setAdminProspectPublicResponseProcessing,
} from '../../services/admin/prospectFollowUpWorkspaceService.js';
import { getAdminProspect } from '../../services/admin/prospectsService';
import { ADMIN_PROSPECT_PUBLIC_RESPONSE_ANCHOR } from './adminProspectQuickFollowUp.js';
import {
  ADMIN_DATE_PLACEHOLDER,
  adminProspectListErrorKey,
  adminProspectStatusLabel,
  classifyAdminCallableError,
  formatAdminDate,
} from './adminProspectDisplay';
import AdminProspectFollowUpWorkspaceQuickActions from './AdminProspectFollowUpWorkspaceQuickActions.jsx';
import {
  createEditorSession,
  nextEditorGeneration,
  nextResponsesEpoch,
  planResponsesAfterLeavingQueue,
  responseActionVisibility,
  responsesEmptyIsDefinitive,
  runWorkspaceEditorRefresh,
  settleAbandonedListBusy,
  settleListLoadOutcome,
  shouldApplyResponsesLoad,
  shouldShowListErrorBanner,
  shouldShowLoadMore,
  silentRefreshBusyStatus,
  toEditorProspect,
} from './adminProspectFollowUpWorkspace.js';

function intentLabel(intent, t) {
  const key = `admin.prospection.followUpWorkspace.intent.${intent}`;
  const label = t(key);
  return label === key ? intent : label;
}

function ProspectRowLink({
  prospectId,
  children,
  focusPublicResponses = false,
  campaignId = null,
  mailingId = null,
  intent = null,
}) {
  // HashRouter: never put a document fragment on `to` — the route already lives in location.hash.
  // React Router: location state must be a separate `state` prop, not nested inside `to`.
  const pathname = `/admin/prospection/${prospectId}`;
  const linkState = focusPublicResponses
    ? {
        focusPublicResponseAnchor: ADMIN_PROSPECT_PUBLIC_RESPONSE_ANCHOR,
        ...(campaignId ? { focusPublicResponseCampaignId: campaignId } : {}),
        ...(mailingId ? { focusPublicResponseMailingId: mailingId } : {}),
        ...(intent ? { focusPublicResponseIntent: intent } : {}),
      }
    : undefined;
  return (
    <Link
      to={pathname}
      state={linkState}
      className="font-medium text-indigo-700 underline-offset-2 hover:underline"
    >
      {children}
    </Link>
  );
}

export default function AdminProspectFollowUpWorkspacePage() {
  const { t, language } = useLanguage();
  const titleId = useId();
  const responsesHeadingId = useId();
  const followUpsHeadingId = useId();
  const opportunitiesHeadingId = useId();
  const editorHeadingId = useId();

  const [responsesFilter, setResponsesFilter] = useState('needs_processing');
  const [responsesStatus, setResponsesStatus] = useState('loading');
  const [responses, setResponses] = useState([]);
  const [responsesError, setResponsesError] = useState(null);
  const [responsesActionError, setResponsesActionError] = useState(null);
  const [responsesNextToken, setResponsesNextToken] = useState(null);
  const [responsesCoverage, setResponsesCoverage] = useState('unknown');
  const [processingBusyId, setProcessingBusyId] = useState(null);

  const [followUpBucket, setFollowUpBucket] = useState('overdue');
  const [followUpsStatus, setFollowUpsStatus] = useState('loading');
  const [followUps, setFollowUps] = useState([]);
  const [followUpsError, setFollowUpsError] = useState(null);
  const [followUpsNextToken, setFollowUpsNextToken] = useState(null);
  const [followUpsTimeZone, setFollowUpsTimeZone] = useState(null);

  const [opportunityStatus, setOpportunityStatus] = useState('all');
  const [oppsStatus, setOppsStatus] = useState('loading');
  const [opportunities, setOpportunities] = useState([]);
  const [oppsError, setOppsError] = useState(null);
  const [oppsNextToken, setOppsNextToken] = useState(null);

  // Independently mounted editor — survives list remounts/refreshes.
  const [editorProspect, setEditorProspect] = useState(null);

  const mountedRef = useRef(true);
  const responsesSeq = useRef(0);
  const followUpsSeq = useRef(0);
  const oppsSeq = useRef(0);
  const processSeq = useRef(0);
  const responsesEpochRef = useRef(0);
  const responsesNextTokenRef = useRef(null);
  const responsesItemsRef = useRef([]);
  const responsesStatusRef = useRef(responsesStatus);
  const responsesFilterRef = useRef(responsesFilter);
  const followUpsItemsRef = useRef([]);
  const followUpsStatusRef = useRef(followUpsStatus);
  const oppsItemsRef = useRef([]);
  const oppsStatusRef = useRef(oppsStatus);
  const followUpBucketRef = useRef(followUpBucket);
  const opportunityStatusRef = useRef(opportunityStatus);
  /** @type {React.MutableRefObject<{ prospectId: string, generation: number } | null>} */
  const editorSessionRef = useRef(null);
  const editorGenerationRef = useRef(0);
  const editorProspectRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      editorGenerationRef.current = nextEditorGeneration(editorGenerationRef.current);
      editorSessionRef.current = null;
    };
  }, []);

  useEffect(() => {
    responsesNextTokenRef.current = responsesNextToken;
  }, [responsesNextToken]);

  useEffect(() => {
    responsesItemsRef.current = responses;
  }, [responses]);

  useEffect(() => {
    responsesStatusRef.current = responsesStatus;
  }, [responsesStatus]);

  useEffect(() => {
    responsesFilterRef.current = responsesFilter;
  }, [responsesFilter]);

  useEffect(() => {
    followUpsItemsRef.current = followUps;
  }, [followUps]);

  useEffect(() => {
    followUpsStatusRef.current = followUpsStatus;
  }, [followUpsStatus]);

  useEffect(() => {
    oppsItemsRef.current = opportunities;
  }, [opportunities]);

  useEffect(() => {
    oppsStatusRef.current = oppsStatus;
  }, [oppsStatus]);

  useEffect(() => {
    followUpBucketRef.current = followUpBucket;
  }, [followUpBucket]);

  useEffect(() => {
    opportunityStatusRef.current = opportunityStatus;
  }, [opportunityStatus]);

  useEffect(() => {
    editorProspectRef.current = editorProspect;
  }, [editorProspect]);

  const loadResponses = useCallback(
    async ({
      pageToken = null,
      append = false,
      processingState = responsesFilterRef.current,
      silent = false,
    } = {}) => {
      const requestId = ++responsesSeq.current;
      const epochAtStart = responsesEpochRef.current;
      const previousStatus = responsesStatusRef.current;
      const itemCount = responsesItemsRef.current.length;
      if (!silent) {
        setResponsesStatus(append ? 'paging' : 'loading');
      } else if (!append) {
        setResponsesStatus(silentRefreshBusyStatus(previousStatus));
      }
      if (!silent) setResponsesError(null);
      try {
        const data = await listAdminProspectResponsesToProcess(
          buildListAdminProspectResponsesToProcessRequest({
            pageSize: ADMIN_PROSPECT_FOLLOW_UP_WORKSPACE_PAGE_SIZE,
            pageToken,
            processingState,
          }),
        );
        const mayApply =
          requestId === responsesSeq.current &&
          shouldApplyResponsesLoad({
            epochAtStart,
            currentEpoch: responsesEpochRef.current,
            mounted: mountedRef.current,
            filterAtStart: processingState,
            currentFilter: responsesFilterRef.current,
          });
        if (!mayApply) {
          const busy = settleAbandonedListBusy({
            ownsRequestSlot: requestId === responsesSeq.current,
            mounted: mountedRef.current,
            previousStatus,
            itemCount: responsesItemsRef.current.length,
            silent,
          });
          if (busy.shouldSettle) setResponsesStatus(busy.status);
          return settleListLoadOutcome({
            abandoned: true,
            previousStatus,
            itemCount,
          });
        }
        const items = Array.isArray(data?.items) ? data.items : [];
        const outcome = settleListLoadOutcome({
          ok: true,
          silent,
          append,
          items,
          nextPageToken: data?.nextPageToken || null,
          previousStatus,
          itemCount,
        });
        setResponses((prev) => {
          const next = append ? [...prev, ...items] : items;
          responsesItemsRef.current = next;
          return next;
        });
        setResponsesNextToken(outcome.nextPageToken);
        setResponsesCoverage(data?.coverage || 'unknown');
        setResponsesStatus(outcome.status);
        if (outcome.ok) setResponsesError(null);
        return outcome;
      } catch (err) {
        const mayApply =
          requestId === responsesSeq.current &&
          shouldApplyResponsesLoad({
            epochAtStart,
            currentEpoch: responsesEpochRef.current,
            mounted: mountedRef.current,
            filterAtStart: processingState,
            currentFilter: responsesFilterRef.current,
          });
        if (!mayApply) {
          const busy = settleAbandonedListBusy({
            ownsRequestSlot: requestId === responsesSeq.current,
            mounted: mountedRef.current,
            previousStatus,
            itemCount: responsesItemsRef.current.length,
            silent,
          });
          if (busy.shouldSettle) setResponsesStatus(busy.status);
          return settleListLoadOutcome({
            abandoned: true,
            previousStatus,
            itemCount,
          });
        }
        const errorKind = classifyAdminCallableError(err);
        const outcome = settleListLoadOutcome({
          ok: false,
          silent,
          errorKind,
          previousStatus,
          itemCount,
        });
        setResponsesError(errorKind);
        setResponsesStatus(outcome.status);
        return outcome;
      }
    },
    [],
  );

  const loadFollowUps = useCallback(
    async ({
      bucket = followUpBucketRef.current,
      pageToken = null,
      append = false,
      silent = false,
    } = {}) => {
      const requestId = ++followUpsSeq.current;
      const previousStatus = followUpsStatusRef.current;
      const itemCount = followUpsItemsRef.current.length;
      if (!silent) {
        setFollowUpsStatus(append ? 'paging' : 'loading');
      } else if (!append) {
        setFollowUpsStatus(silentRefreshBusyStatus(previousStatus));
      }
      if (!silent) setFollowUpsError(null);
      try {
        const data = await listAdminProspectFollowUps(
          buildListAdminProspectFollowUpsRequest({
            bucket,
            pageSize: ADMIN_PROSPECT_FOLLOW_UP_WORKSPACE_PAGE_SIZE,
            pageToken,
          }),
        );
        if (
          !mountedRef.current ||
          requestId !== followUpsSeq.current ||
          bucket !== followUpBucketRef.current
        ) {
          return settleListLoadOutcome({
            abandoned: true,
            previousStatus,
            itemCount,
          });
        }
        const items = Array.isArray(data?.items) ? data.items : [];
        const outcome = settleListLoadOutcome({
          ok: true,
          silent,
          append,
          items,
          nextPageToken: data?.nextPageToken || null,
          previousStatus,
          itemCount,
        });
        setFollowUps((prev) => {
          const next = append ? [...prev, ...items] : items;
          followUpsItemsRef.current = next;
          return next;
        });
        setFollowUpsNextToken(outcome.nextPageToken);
        setFollowUpsTimeZone(data?.timeZone || null);
        setFollowUpsStatus(outcome.status);
        if (outcome.ok) setFollowUpsError(null);
        return outcome;
      } catch (err) {
        if (
          !mountedRef.current ||
          requestId !== followUpsSeq.current ||
          bucket !== followUpBucketRef.current
        ) {
          return settleListLoadOutcome({
            abandoned: true,
            previousStatus,
            itemCount,
          });
        }
        const errorKind = classifyAdminCallableError(err);
        const outcome = settleListLoadOutcome({
          ok: false,
          silent,
          errorKind,
          previousStatus,
          itemCount,
        });
        setFollowUpsError(errorKind);
        setFollowUpsStatus(outcome.status);
        return outcome;
      }
    },
    [],
  );

  const loadOpportunities = useCallback(
    async ({
      pageToken = null,
      append = false,
      status = opportunityStatusRef.current,
      silent = false,
    } = {}) => {
      const requestId = ++oppsSeq.current;
      const previousStatus = oppsStatusRef.current;
      const itemCount = oppsItemsRef.current.length;
      if (!silent) {
        setOppsStatus(append ? 'paging' : 'loading');
      } else if (!append) {
        setOppsStatus(silentRefreshBusyStatus(previousStatus));
      }
      if (!silent) setOppsError(null);
      try {
        const data = await listAdminProspectActiveOpportunities(
          buildListAdminProspectActiveOpportunitiesRequest({
            pageSize: ADMIN_PROSPECT_FOLLOW_UP_WORKSPACE_PAGE_SIZE,
            pageToken,
            status: status === 'all' ? null : status,
          }),
        );
        if (
          !mountedRef.current ||
          requestId !== oppsSeq.current ||
          status !== opportunityStatusRef.current
        ) {
          return settleListLoadOutcome({
            abandoned: true,
            previousStatus,
            itemCount,
          });
        }
        const items = Array.isArray(data?.items) ? data.items : [];
        const outcome = settleListLoadOutcome({
          ok: true,
          silent,
          append,
          items,
          nextPageToken: data?.nextPageToken || null,
          previousStatus,
          itemCount,
        });
        setOpportunities((prev) => {
          const next = append ? [...prev, ...items] : items;
          oppsItemsRef.current = next;
          return next;
        });
        setOppsNextToken(outcome.nextPageToken);
        setOppsStatus(outcome.status);
        if (outcome.ok) setOppsError(null);
        return outcome;
      } catch (err) {
        if (
          !mountedRef.current ||
          requestId !== oppsSeq.current ||
          status !== opportunityStatusRef.current
        ) {
          return settleListLoadOutcome({
            abandoned: true,
            previousStatus,
            itemCount,
          });
        }
        const errorKind = classifyAdminCallableError(err);
        const outcome = settleListLoadOutcome({
          ok: false,
          silent,
          errorKind,
          previousStatus,
          itemCount,
        });
        setOppsError(errorKind);
        setOppsStatus(outcome.status);
        return outcome;
      }
    },
    [],
  );

  useEffect(() => {
    responsesEpochRef.current = nextResponsesEpoch(responsesEpochRef.current);
    loadResponses({ processingState: responsesFilter });
  }, [responsesFilter, loadResponses]);

  useEffect(() => {
    loadFollowUps({ bucket: followUpBucket });
  }, [followUpBucket, loadFollowUps]);

  useEffect(() => {
    loadOpportunities({ status: opportunityStatus });
  }, [opportunityStatus, loadOpportunities]);

  const openEditor = (prospect) => {
    if (!prospect?.id) return;
    const generation = nextEditorGeneration(editorGenerationRef.current);
    editorGenerationRef.current = generation;
    editorSessionRef.current = createEditorSession({
      prospectId: prospect.id,
      generation,
    });
    setEditorProspect(prospect);
  };

  const closeEditor = () => {
    editorGenerationRef.current = nextEditorGeneration(editorGenerationRef.current);
    editorSessionRef.current = null;
    setEditorProspect(null);
  };

  const handleProcess = async (item, processingState) => {
    if (!item?.campaignId || !item?.mailingId || !item?.intent) return;
    const busyKey = `${item.id}:${processingState}`;
    const requestId = ++processSeq.current;
    const filterAtStart = responsesFilterRef.current;
    setProcessingBusyId(busyKey);
    setResponsesActionError(null);
    try {
      const result = await setAdminProspectPublicResponseProcessing({
        campaignId: item.campaignId,
        mailingId: item.mailingId,
        intent: item.intent,
        processingState,
        expectedUpdatedAtVersion: item.updatedAtVersion,
      });
      if (!mountedRef.current || requestId !== processSeq.current) return;
      if (filterAtStart !== responsesFilterRef.current) return;

      const next = result?.item;
      const leavingQueue =
        (filterAtStart === 'needs_processing' && processingState === 'processed') ||
        (filterAtStart === 'processed' && processingState === 'needs_processing');

      // Invalidate in-flight list reads before applying local removal.
      responsesEpochRef.current = nextResponsesEpoch(responsesEpochRef.current);

      if (leavingQueue) {
        const planned = planResponsesAfterLeavingQueue({
          previousItems: responsesItemsRef.current,
          itemId: item.id,
          nextPageToken: responsesNextTokenRef.current,
        });
        responsesItemsRef.current = planned.items;
        setResponses(planned.items);
        setResponsesStatus(planned.status);
        if (planned.shouldLoadMore && planned.pageToken) {
          await loadResponses({
            pageToken: planned.pageToken,
            append: false,
            processingState: filterAtStart,
            silent: true,
          });
        }
      } else if (next) {
        const mapped = responsesItemsRef.current.map((row) =>
          row.id === item.id ? next : row,
        );
        responsesItemsRef.current = mapped;
        setResponses(mapped);
      }
    } catch (err) {
      if (!mountedRef.current || requestId !== processSeq.current) return;
      setResponsesActionError(classifyAdminCallableError(err));
    } finally {
      if (mountedRef.current && requestId === processSeq.current) {
        setProcessingBusyId(null);
      }
    }
  };

  const refreshFollowUpRow = async ({ prospectId, conflictReload = false } = {}) => {
    return runWorkspaceEditorRefresh({
      prospectId,
      conflictReload,
      getSession: () => editorSessionRef.current,
      getMounted: () => mountedRef.current,
      getCurrentEditorProspect: () => editorProspectRef.current,
      onEditorProspectAccepted: (prospect) => {
        editorProspectRef.current = prospect;
        setEditorProspect(prospect);
      },
      loadDetail: async (ownerId) => {
        const detail = await getAdminProspect(ownerId);
        return toEditorProspect(detail);
      },
      loadFollowUps: () =>
        loadFollowUps({ bucket: followUpBucketRef.current, silent: true }),
      loadOpportunities: () =>
        loadOpportunities({ status: opportunityStatusRef.current, silent: true }),
    });
  };

  const responsesListVisible =
    responses.length > 0 &&
    (responsesStatus === 'ready' ||
      responsesStatus === 'paging' ||
      responsesStatus === 'refreshing');

  const followUpsListVisible =
    followUps.length > 0 &&
    (followUpsStatus === 'ready' ||
      followUpsStatus === 'paging' ||
      followUpsStatus === 'refreshing');

  const oppsListVisible =
    opportunities.length > 0 &&
    (oppsStatus === 'ready' || oppsStatus === 'paging' || oppsStatus === 'refreshing');

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <header className="flex items-start gap-2">
        <ClipboardList className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" aria-hidden="true" />
        <div>
          <h1 id={titleId} className="text-xl font-semibold text-slate-900">
            {t('admin.prospection.followUpWorkspace.title')}
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {t('admin.prospection.followUpWorkspace.subtitle')}
          </p>
        </div>
      </header>

      {editorProspect ? (
        <section aria-labelledby={editorHeadingId} className="space-y-2">
          <h2 id={editorHeadingId} className="text-base font-semibold text-slate-900">
            {t('admin.prospection.followUpWorkspace.quickActions.editorTitle')}
          </h2>
          <p className="text-sm text-slate-600">
            {[editorProspect.address, editorProspect.city].filter(Boolean).join(', ') ||
              editorProspect.id}
          </p>
          <AdminProspectFollowUpWorkspaceQuickActions
            key={editorProspect.id}
            prospect={editorProspect}
            language={language}
            t={t}
            onSaved={refreshFollowUpRow}
            onClose={closeEditor}
            initiallyExpanded
          />
        </section>
      ) : null}

      <section aria-labelledby={responsesHeadingId} className="space-y-3">
        <div>
          <h2 id={responsesHeadingId} className="text-lg font-semibold text-slate-900">
            {t('admin.prospection.followUpWorkspace.responses.title')}
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            {t('admin.prospection.followUpWorkspace.responses.subtitle')}
          </p>
        </div>
        <div
          role="tablist"
          aria-label={t('admin.prospection.followUpWorkspace.responses.filters')}
          className="flex flex-wrap gap-2"
        >
          {ADMIN_PROSPECT_PUBLIC_RESPONSE_PROCESSING_LIST_STATES.map((state) => {
            const selected = responsesFilter === state;
            return (
              <button
                key={state}
                type="button"
                role="tab"
                aria-selected={selected}
                className={`rounded-md px-3 py-1.5 text-sm ${
                  selected
                    ? 'bg-slate-900 text-white'
                    : 'border border-slate-300 bg-white text-slate-700'
                }`}
                onClick={() => setResponsesFilter(state)}
              >
                {t(`admin.prospection.followUpWorkspace.responses.filter.${state}`)}
              </button>
            );
          })}
        </div>
        {responsesCoverage === 'incomplete' || responsesCoverage === 'unknown' ? (
          <p
            className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950"
            role="status"
          >
            {t('admin.prospection.followUpWorkspace.responses.coverageIncomplete')}
          </p>
        ) : null}
        <div
          aria-busy={
            responsesStatus === 'loading' ||
            responsesStatus === 'paging' ||
            responsesStatus === 'refreshing'
          }
        >
          {responsesStatus === 'loading' && responses.length === 0 ? (
            <p className="text-sm text-slate-500" aria-live="polite">
              {t('admin.prospection.followUpWorkspace.loading')}
            </p>
          ) : null}
          {shouldShowListErrorBanner(responsesStatus, responsesError) ? (
            <p className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" role="alert">
              {t(adminProspectListErrorKey(responsesError))}
              <button
                type="button"
                className="ml-2 font-medium underline"
                onClick={() => loadResponses({ processingState: responsesFilter })}
              >
                {t('admin.prospection.followUpWorkspace.retry')}
              </button>
            </p>
          ) : null}
          {responsesActionError ? (
            <p className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" role="alert">
              {t(adminProspectListErrorKey(responsesActionError))}
            </p>
          ) : null}
          {responsesStatus === 'empty' ? (
            <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
              {responsesEmptyIsDefinitive(responsesCoverage)
                ? t('admin.prospection.followUpWorkspace.responses.empty')
                : t('admin.prospection.followUpWorkspace.responses.emptyCoverageUnknown')}
            </p>
          ) : null}
          {responsesListVisible ? (
            <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
              {responses.map((item) => {
                const processBusy = processingBusyId === `${item.id}:processed`;
                const reopenBusy = processingBusyId === `${item.id}:needs_processing`;
                const { showProcess, showReopen } = responseActionVisibility(
                  responsesFilter,
                  item.processingState || responsesFilter,
                );
                return (
                  <li key={item.id} className="px-4 py-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 space-y-1">
                        {item.prospectId ? (
                          <ProspectRowLink
                            prospectId={item.prospectId}
                            focusPublicResponses
                            campaignId={item.campaignId}
                            mailingId={item.mailingId}
                            intent={item.intent}
                          >
                            {[item.address, item.city].filter(Boolean).join(', ') ||
                              t('admin.prospection.followUpWorkspace.openProspect')}
                          </ProspectRowLink>
                        ) : (
                          <span className="font-medium text-slate-800">
                            {[item.address, item.city].filter(Boolean).join(', ') || '—'}
                          </span>
                        )}
                        <p className="text-sm text-slate-600">
                          {intentLabel(item.intent, t)}
                          {item.ownerName ? ` · ${item.ownerName}` : ''}
                          {item.prospectStatus
                            ? ` · ${adminProspectStatusLabel(item.prospectStatus, t)}`
                            : ''}
                        </p>
                        <p className="text-xs text-slate-500">
                          {t('admin.prospection.followUpWorkspace.responses.submittedAt')}:{' '}
                          {item.submittedAt
                            ? formatAdminDate(item.submittedAt, language)
                            : ADMIN_DATE_PLACEHOLDER}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {showProcess ? (
                          <button
                            type="button"
                            className="rounded-md bg-slate-900 px-2.5 py-1.5 text-sm text-white disabled:opacity-50"
                            disabled={Boolean(processingBusyId)}
                            aria-busy={processBusy}
                            aria-label={t('admin.prospection.followUpWorkspace.responses.process')}
                            onClick={() => handleProcess(item, 'processed')}
                          >
                            {t('admin.prospection.followUpWorkspace.responses.process')}
                          </button>
                        ) : null}
                        {showReopen ? (
                          <button
                            type="button"
                            className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm text-slate-700 disabled:opacity-50"
                            disabled={Boolean(processingBusyId)}
                            aria-busy={reopenBusy}
                            aria-label={t('admin.prospection.followUpWorkspace.responses.reopen')}
                            onClick={() => handleProcess(item, 'needs_processing')}
                          >
                            {t('admin.prospection.followUpWorkspace.responses.reopen')}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : null}
          {shouldShowLoadMore({
            status: responsesStatus,
            nextPageToken: responsesNextToken,
            itemCount: responses.length,
          }) ? (
            <button
              type="button"
              className="text-sm font-medium text-indigo-700 underline"
              onClick={() =>
                loadResponses({
                  pageToken: responsesNextToken,
                  append: true,
                  processingState: responsesFilter,
                })
              }
            >
              {t('admin.prospection.followUpWorkspace.loadMore')}
            </button>
          ) : null}
        </div>
      </section>

      <section aria-labelledby={followUpsHeadingId} className="space-y-3">
        <div>
          <h2 id={followUpsHeadingId} className="text-lg font-semibold text-slate-900">
            {t('admin.prospection.followUpWorkspace.followUps.title')}
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            {t('admin.prospection.followUpWorkspace.followUps.subtitle')}
            {followUpsTimeZone ? ` (${followUpsTimeZone})` : ''}
          </p>
        </div>
        <div
          role="tablist"
          aria-label={t('admin.prospection.followUpWorkspace.followUps.filters')}
          className="flex flex-wrap gap-2"
        >
          {ADMIN_PROSPECT_FOLLOW_UP_BUCKETS.map((bucket) => {
            const selected = followUpBucket === bucket;
            return (
              <button
                key={bucket}
                type="button"
                role="tab"
                aria-selected={selected}
                className={`rounded-md px-3 py-1.5 text-sm ${
                  selected
                    ? 'bg-slate-900 text-white'
                    : 'border border-slate-300 bg-white text-slate-700'
                }`}
                onClick={() => setFollowUpBucket(bucket)}
              >
                {t(`admin.prospection.followUpWorkspace.followUps.bucket.${bucket}`)}
              </button>
            );
          })}
        </div>
        <div
          aria-busy={
            followUpsStatus === 'loading' ||
            followUpsStatus === 'paging' ||
            followUpsStatus === 'refreshing'
          }
        >
          {followUpsStatus === 'loading' && followUps.length === 0 ? (
            <p className="text-sm text-slate-500" aria-live="polite">
              {t('admin.prospection.followUpWorkspace.loading')}
            </p>
          ) : null}
          {shouldShowListErrorBanner(followUpsStatus, followUpsError) ? (
            <p className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" role="alert">
              {t(adminProspectListErrorKey(followUpsError))}
              <button
                type="button"
                className="ml-2 font-medium underline"
                onClick={() => loadFollowUps({ bucket: followUpBucket })}
              >
                {t('admin.prospection.followUpWorkspace.retry')}
              </button>
            </p>
          ) : null}
          {followUpsStatus === 'empty' ? (
            <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
              {t('admin.prospection.followUpWorkspace.followUps.empty')}
            </p>
          ) : null}
          {followUpsListVisible ? (
            <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
              {followUps.map((item) => (
                <li key={item.id} className="px-4 py-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 space-y-1">
                      <ProspectRowLink prospectId={item.id}>
                        {[item.address, item.city].filter(Boolean).join(', ') || item.id}
                      </ProspectRowLink>
                      <p className="text-sm text-slate-600">
                        {adminProspectStatusLabel(item.status, t)}
                        {item.ownerName ? ` · ${item.ownerName}` : ''}
                      </p>
                      <p className="text-xs text-slate-500">
                        {t('admin.prospection.followUpWorkspace.followUps.nextAt')}:{' '}
                        {item.nextFollowUpAt
                          ? formatAdminDate(item.nextFollowUpAt, language)
                          : ADMIN_DATE_PLACEHOLDER}
                        {item.followUpBucket
                          ? ` · ${t(`admin.prospection.followUpWorkspace.followUps.bucket.${item.followUpBucket}`)}`
                          : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="text-sm font-medium text-indigo-700 underline-offset-2 hover:underline"
                      onClick={() => openEditor(item)}
                    >
                      {t('admin.prospection.followUpWorkspace.quickActions.show')}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
          {shouldShowLoadMore({
            status: followUpsStatus,
            nextPageToken: followUpsNextToken,
            itemCount: followUps.length,
          }) ? (
            <button
              type="button"
              className="text-sm font-medium text-indigo-700 underline"
              onClick={() =>
                loadFollowUps({
                  bucket: followUpBucket,
                  pageToken: followUpsNextToken,
                  append: true,
                })
              }
            >
              {t('admin.prospection.followUpWorkspace.loadMore')}
            </button>
          ) : null}
        </div>
      </section>

      <section aria-labelledby={opportunitiesHeadingId} className="space-y-3">
        <div>
          <h2 id={opportunitiesHeadingId} className="text-lg font-semibold text-slate-900">
            {t('admin.prospection.followUpWorkspace.opportunities.title')}
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            {t('admin.prospection.followUpWorkspace.opportunities.subtitle')}
          </p>
        </div>
        <div
          role="tablist"
          aria-label={t('admin.prospection.followUpWorkspace.opportunities.filters')}
          className="flex flex-wrap gap-2"
        >
          <button
            type="button"
            role="tab"
            aria-selected={opportunityStatus === 'all'}
            className={`rounded-md px-3 py-1.5 text-sm ${
              opportunityStatus === 'all'
                ? 'bg-slate-900 text-white'
                : 'border border-slate-300 bg-white text-slate-700'
            }`}
            onClick={() => setOpportunityStatus('all')}
          >
            {t('admin.prospection.followUpWorkspace.opportunities.filter.all')}
          </button>
          {ADMIN_PROSPECT_ACTIVE_OPPORTUNITY_STATUSES.map((status) => {
            const selected = opportunityStatus === status;
            return (
              <button
                key={status}
                type="button"
                role="tab"
                aria-selected={selected}
                className={`rounded-md px-3 py-1.5 text-sm ${
                  selected
                    ? 'bg-slate-900 text-white'
                    : 'border border-slate-300 bg-white text-slate-700'
                }`}
                onClick={() => setOpportunityStatus(status)}
              >
                {adminProspectStatusLabel(status, t)}
              </button>
            );
          })}
        </div>
        <div
          aria-busy={
            oppsStatus === 'loading' || oppsStatus === 'paging' || oppsStatus === 'refreshing'
          }
        >
          {oppsStatus === 'loading' && opportunities.length === 0 ? (
            <p className="text-sm text-slate-500" aria-live="polite">
              {t('admin.prospection.followUpWorkspace.loading')}
            </p>
          ) : null}
          {shouldShowListErrorBanner(oppsStatus, oppsError) ? (
            <p className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" role="alert">
              {t(adminProspectListErrorKey(oppsError))}
              <button
                type="button"
                className="ml-2 font-medium underline"
                onClick={() => loadOpportunities({ status: opportunityStatus })}
              >
                {t('admin.prospection.followUpWorkspace.retry')}
              </button>
            </p>
          ) : null}
          {oppsStatus === 'empty' ? (
            <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
              {t('admin.prospection.followUpWorkspace.opportunities.empty')}
            </p>
          ) : null}
          {oppsListVisible ? (
            <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
              {opportunities.map((item) => (
                <li key={item.id} className="px-4 py-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 space-y-1">
                      <ProspectRowLink prospectId={item.id}>
                        {[item.address, item.city].filter(Boolean).join(', ') || item.id}
                      </ProspectRowLink>
                      <p className="text-sm text-slate-600">
                        {adminProspectStatusLabel(item.status, t)}
                        {item.ownerName ? ` · ${item.ownerName}` : ''}
                      </p>
                      <p className="text-xs text-slate-500">
                        {t('admin.prospection.followUpWorkspace.opportunities.updatedAt')}:{' '}
                        {item.updatedAt
                          ? formatAdminDate(item.updatedAt, language)
                          : ADMIN_DATE_PLACEHOLDER}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="text-sm font-medium text-indigo-700 underline-offset-2 hover:underline"
                      onClick={() => openEditor(item)}
                    >
                      {t('admin.prospection.followUpWorkspace.quickActions.show')}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
          {shouldShowLoadMore({
            status: oppsStatus,
            nextPageToken: oppsNextToken,
            itemCount: opportunities.length,
          }) ? (
            <button
              type="button"
              className="text-sm font-medium text-indigo-700 underline"
              onClick={() =>
                loadOpportunities({
                  pageToken: oppsNextToken,
                  append: true,
                  status: opportunityStatus,
                })
              }
            >
              {t('admin.prospection.followUpWorkspace.loadMore')}
            </button>
          ) : null}
        </div>
      </section>
    </div>
  );
}
